/**
 * Cours — Hub des cours de lecture coranique.
 *
 *   /cours        → CoursIndex : 2 cartes (Apprendre à lire, Tajwīd)
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { BookOpenText, Palette, Sparkles, Clock, LineChart, ArrowRight } from 'lucide-react';
import { GlassCard, SectionHeader } from '../components/shared';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { tajwidApi } from '../services/api';

const CourseCard = ({
  to,
  icon: Icon,
  iconBg,
  title,
  arabicTitle,
  subtitle,
  description,
  badge,
  comingSoon = false,
  testid,
}) => {
  const inner = (
    <GlassCard
      className={`h-full transition-all relative overflow-hidden ${
        comingSoon
          ? 'opacity-70 cursor-not-allowed'
          : 'hover:border-primary/40 cursor-pointer hover:-translate-y-0.5'
      }`}
      data-testid={testid}
    >
      <div className="flex items-start gap-4">
        <div
          className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}
        >
          <Icon className="w-7 h-7 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-xl font-serif text-text-primary">{title}</h3>
            {badge && <Badge variant="outline" className="text-xs">{badge}</Badge>}
            {comingSoon && (
              <Badge className="text-xs bg-amber-500/15 text-amber-300 border-amber-500/30">
                <Clock className="w-3 h-3 mr-1" />
                Bientôt
              </Badge>
            )}
          </div>
          {arabicTitle && (
            <p className="font-arabic text-lg text-primary/80 mb-2" dir="rtl">
              {arabicTitle}
            </p>
          )}
          <p className="text-sm text-text-muted mb-3">{subtitle}</p>
          <p className="text-sm text-text-secondary leading-relaxed">{description}</p>
        </div>
      </div>
    </GlassCard>
  );

  if (comingSoon) return <div>{inner}</div>;
  return <Link to={to}>{inner}</Link>;
};

export const CoursIndex = () => {
  const { t } = useTranslation();
  const [tajwidRulesCount, setTajwidRulesCount] = useState(null);
  useEffect(() => {
    tajwidApi.getRules().then(
      (r) => setTajwidRulesCount((r.data || []).length),
      () => setTajwidRulesCount(null),
    );
  }, []);
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="cours-index-page">
      <SectionHeader
        overline={t('cours.overline')}
        title={t('cours.title')}
        subtitle={t('cours.subtitle')}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <CourseCard
            to="/cours/lecture"
            icon={BookOpenText}
            iconBg="bg-primary/15 border border-primary/30"
            title={t('cours.read_title')}
            arabicTitle="تعلّم القراءة"
            subtitle={t('cours.read_subtitle')}
            description={t('cours.read_description')}
            badge={
              <span className="inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                4 {t('cours.modules')}
              </span>
            }
            testid="course-card-read"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
        >
          <CourseCard
            to="/cours/tajwid"
            icon={Palette}
            iconBg="bg-primary/15 border border-primary/30"
            title={t('cours.tajwid_title')}
            arabicTitle="أحكام التجويد"
            subtitle={t('cours.tajwid_subtitle')}
            description={t('cours.tajwid_description')}
            badge={
              <span className="inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                {tajwidRulesCount || 9} {t('cours.rules')}
              </span>
            }
            testid="course-card-tajwid"
          />
        </motion.div>
      </div>

      <Link to="/mes-progres" className="block mt-6" data-testid="cours-mes-progres-link">
        <GlassCard className="flex items-center justify-between gap-4 hover:border-primary/40 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <LineChart className="w-6 h-6 text-emerald-300" />
            </div>
            <div>
              <h3 className="text-lg font-serif text-text-primary">Mes progrès</h3>
              <p className="text-sm text-text-muted">
                Suivez votre apprentissage de la lecture et du tajwīd, avec des recommandations personnalisées.
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm">
            Ouvrir <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </GlassCard>
      </Link>

      <Link to="/devoirs" className="block mt-3" data-testid="cours-devoirs-link">
        <GlassCard className="flex items-center justify-between gap-4 hover:border-primary/40 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-amber-300" />
            </div>
            <div>
              <h3 className="text-lg font-serif text-text-primary">Devoirs</h3>
              <p className="text-sm text-text-muted">
                Recevez ou créez des séries d'exercices ciblés (lettres, compétences, difficulté).
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm">
            Ouvrir <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </GlassCard>
      </Link>
    </div>
  );
};

export default CoursIndex;
